import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Flex,
  Text,
  Tag,
  Select,
  Divider,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { scraperRunLogs, scraperRunLogDetail } from "../../api";

const STATUS_MAP = {
  RUNNING: { label: "진행중", color: "blue" },
  SUCCESS: { label: "성공", color: "green" },
  FAILURE: { label: "실패", color: "red" },
};

export function RunLogsModal({ isOpen, onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 15;
  const toast = useToast();

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await scraperRunLogs({
        skip,
        limit,
        status: statusFilter || undefined,
      });
      setItems(res?.data?.items ?? []);
      setTotal(res?.data?.total ?? 0);
    } catch (e) {
      console.error("로그 조회 실패:", e);
      toast({ title: "로그 조회 실패", status: "error", duration: 2000 });
    } finally {
      setLoading(false);
    }
  }, [skip, statusFilter, toast]);

  useEffect(() => {
    if (isOpen) loadLogs();
  }, [isOpen, loadLogs]);

  const handleLogClick = async (log) => {
    if (selectedLog?.id === log.id) {
      setSelectedLog(null);
      return;
    }
    setSelectedLog(log);
    setDetailLoading(true);
    try {
      const res = await scraperRunLogDetail(log.id);
      setSelectedLog(res?.data ?? log);
    } catch (e) {
      toast({ title: "상세 조회 실패", status: "error", duration: 2000 });
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>수집 로그</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" pb="24px">
          <Flex gap="8px" mb="16px" align="center">
            <Select
              w="120px"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setSkip(0);
              }}
            >
              <option value="">전체</option>
              <option value="SUCCESS">성공</option>
              <option value="FAILURE">실패</option>
              <option value="RUNNING">진행중</option>
            </Select>
          </Flex>

          {loading ? (
            <Flex justify="center" py="40px">
              <Spinner size="lg" />
            </Flex>
          ) : items.length === 0 ? (
            <Text color="gray.500" py="40px" textAlign="center">
              로그가 없습니다.
            </Text>
          ) : (
            <Box>
              <Flex
                py="12px"
                gap="12px"
                bg="#E2EEFF"
                fontWeight="600"
                color="gray.700"
                fontSize="sm"
                borderRadius="8px 8px 0 0"
                px="12px"
              >
                <Box w="60px" flexShrink={0}>상태</Box>
                <Box flex="1" minW="120px">스케줄/작업명</Box>
                <Box flex="1" minW="120px">키워드</Box>
                <Box w="160px" flexShrink={0}>실행 시각</Box>
                <Box w="120px" flexShrink={0}>저장/연결</Box>
                <Box w="60px" flexShrink={0}>소요</Box>
              </Flex>
              <Divider m="0" />
              {items.map((log) => {
                const status = STATUS_MAP[log.status] ?? {
                  label: log.status,
                  color: "gray",
                };
                return (
                  <Box key={log.id}>
                    <Flex
                      align="center"
                      py="12px"
                      gap="12px"
                      px="12px"
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      onClick={() => handleLogClick(log)}
                    >
                      <Box w="60px" flexShrink={0}>
                        <Tag size="sm" colorScheme={status.color}>
                          {status.label}
                        </Tag>
                      </Box>
                      <Text fontSize="sm" fontWeight={500} noOfLines={1} flex="1" minW="120px">
                        {log.jobName ?? `#${log.id}`}
                      </Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={1} flex="1" minW="120px">
                        {log.hashtags}
                      </Text>
                      <Text fontSize="xs" color="gray.600" w="160px" flexShrink={0}>
                        {log.startedAt
                          ? new Date(log.startedAt).toLocaleString("ko-KR")
                          : "-"}
                      </Text>
                      <Text fontSize="xs" color="gray.600" w="120px" flexShrink={0}>
                        저장: {log.savedCount ?? 0} / 연결: {log.tagsLinkedCount ?? 0}
                      </Text>
                      <Box w="60px" flexShrink={0}>
                        {log.durationSeconds != null ? (
                          <Text fontSize="xs" color="gray.500">{log.durationSeconds}초</Text>
                        ) : (
                          <Text fontSize="xs" color="gray.400">-</Text>
                        )}
                      </Box>
                    </Flex>
                    <Divider />
                  </Box>
                );
              })}
            </Box>
          )}

          {totalPages > 1 && (
            <Flex justify="center" gap="8px" mt="16px">
              <Button
                size="sm"
                isDisabled={skip <= 0}
                onClick={() => setSkip((s) => Math.max(0, s - limit))}
              >
                이전
              </Button>
              <Text alignSelf="center" fontSize="14px">
                {currentPage} / {totalPages} (총 {total}건)
              </Text>
              <Button
                size="sm"
                isDisabled={skip + limit >= total}
                onClick={() => setSkip((s) => s + limit)}
              >
                다음
              </Button>
            </Flex>
          )}

          {selectedLog && (
            <Box
              mt="24px"
              p="16px"
              bg="gray.50"
              borderRadius="8px"
              border="1px solid"
              borderColor="gray.200"
            >
              <Text fontWeight="600" mb="12px">
                로그 상세 #{selectedLog.id}
              </Text>
              {detailLoading ? (
                <Spinner size="sm" />
              ) : (
                <Box fontSize="sm" as="pre" whiteSpace="pre-wrap" overflowX="auto">
                  {JSON.stringify(selectedLog, null, 2)}
                </Box>
              )}
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
